import type { SearchClient } from 'algoliasearch';
import dayjs from 'dayjs';
import type { AlgoliaHit, SearchOptions } from 'instantsearch.js';
import type { ParsedUrlQuery } from 'querystring';
import type { InstantSearchServerState } from 'react-instantsearch';

import type { Country, Locale } from '@/types/regionTypes';
import { getIntParam, getSearchStringFromAsPath } from '@/utils/urlUtils';

import type { CountryCode, LanguageCodeEnum } from '@/graphql/saleor/api';
import type { ProductHit } from '@/integrations/algolia/types';
import { ENVIRONMENT_NAME, NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { paths } from '@/lib/paths';
import { getRegionFromLocale } from '@/lib/regions';
import { getStrapMaterial, supportedStrapMaterials } from '@/lib/straps';

import { createSearchClient } from './searchClientBuilder';
// Re-export sendAlgoliaEvent from its own module to maintain backward compatibility
export { sendAlgoliaEvent } from './sendAlgoliaEvent';
import { useMemo } from 'react';

import { useRouter } from '@/hooks/useRouter';

import type {
  ALGOLIA_INDEX_DATA_TYPE,
  EyewearHit,
  SearchHit,
  SearchUiState,
  StrapHit,
  WatchHit,
} from './types';

export type ProductType = 'Watch' | 'Strap' | 'Store' | 'Eyewear';
export type SearchUiStateWithLocale = SearchUiState & { locale?: Locale };

export const generateWatchDetailPagePath = ({
  collections,
  model,
  objectID,
}: Pick<WatchHit, 'collections' | 'model' | 'objectID'>) => {
  if (collections?.length === 0) {
    return '#';
  }
  return paths.watches.collection.model.product.asPath({
    model: model?.slug,
    collection: collections?.[0]?.slug?.toLowerCase(),
    sku: objectID,
  });
};

export const generateStrapDetailPagePath = ({
  material_en,
  objectID,
}: StrapHit) => {
  const productMaterial = getStrapMaterial(material_en);

  if (!productMaterial) {
    throw new Error(
      `Strap with material: ${material_en} is not supported and cannot be displayed`,
    );
  }

  return paths.straps.material.product.asPath({
    material: productMaterial,
    sku: objectID,
  });
};

export const normalizeAccentedChars = (text: string): string => {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const generateEyewearDetailPagePath = ({
  name_en,
  objectID,
}: Pick<EyewearHit, 'name_en' | 'objectID'>) => {
  return paths.sunglasses.product.asPath({
    modelSku: normalizeAccentedChars(
      `${name_en?.toLowerCase().replace(' ', '-')}-${objectID}`,
    ),
  });
};

const matchesField = (
  fieldName: string,
  key: string,
  exclude?: string,
): boolean => {
  const baseRegex = new RegExp(`^${fieldName}(\\.|$)`); // Match the exact field name followed by "." or end of string
  const excludeRegex = exclude ? new RegExp(exclude) : null; // Optional regex for excluding unwanted patterns
  return baseRegex.test(key) && (!excludeRegex || !excludeRegex.test(key));
};

/**
 * Parses a v2 facet value in the format "EnglishID | LocalizedLabel"
 * Returns both the ID (English) and the label (localized), trimmed of whitespace
 */
function parseV2FacetValue(v2Value: string): { id: string; label: string } {
  const pipeIndex = v2Value.indexOf('|');
  if (pipeIndex === -1) {
    return { id: v2Value.trim(), label: v2Value.trim() };
  }
  const id = v2Value.substring(0, pipeIndex).trim();
  const label = v2Value.substring(pipeIndex + 1).trim();
  return { id, label };
}

/**
 * Builds translation maps from v2 facet values.
 * v2 facets have values like "Men|Herren" where "Men" is the English ID
 * and "Herren" is the localized label.
 *
 * @returns Maps for bidirectional translation between English IDs and localized labels
 */
function buildTranslationMapsFromV2Facets(v2Facet: Record<string, number>): {
  idToLabel: Map<string, string>;
  labelToId: Map<string, string>;
} {
  const idToLabel = new Map<string, string>();
  const labelToId = new Map<string, string>();

  Object.keys(v2Facet).forEach((v2Value) => {
    const { id, label } = parseV2FacetValue(v2Value);
    idToLabel.set(id.toLowerCase(), label);
    labelToId.set(label.toLowerCase(), id);
  });

  return { idToLabel, labelToId };
}

/**
 * Gets the v2 facet key for a given field and locale.
 * For "type.ES", this returns "type_v2.ES"
 */
function getV2FacetKey(fieldName: string, localeCode: string): string {
  return `${fieldName}_v2.${localeCode}`;
}

/**
 * Translates values using v2 facet ID-based approach.
 * This is more reliable than index-based translation as it doesn't depend on ordering.
 *
 * @param values - The values to translate (either English IDs or localized labels)
 * @param sourceV2Facet - The v2 facet from the source locale
 * @param targetV2Facet - The v2 facet from the target locale
 * @param direction - 'toEnglish' translates localized labels to English IDs,
 *                    'fromEnglish' translates English IDs to localized labels
 */
function translateUsingV2Facets(
  values: string[],
  sourceV2Facet: Record<string, number>,
  targetV2Facet: Record<string, number>,
  direction: 'toEnglish' | 'fromEnglish',
): string[] {
  const sourceMaps = buildTranslationMapsFromV2Facets(sourceV2Facet);

  // Build a map from English ID (lowercase) to the full v2 value in target facet
  const targetIdToV2Value = new Map<string, string>();
  Object.keys(targetV2Facet).forEach((v2Value) => {
    const { id } = parseV2FacetValue(v2Value);
    targetIdToV2Value.set(id.toLowerCase(), v2Value);
  });

  return values
    .map((value) => {
      // Use lowercase for case-insensitive lookup
      const valueLower = value.toLowerCase();
      if (direction === 'toEnglish') {
        // Translate localized label to English ID
        // First, find the English ID from the source locale
        const englishId = sourceMaps.labelToId.get(valueLower);
        if (englishId) {
          return englishId;
        }
      } else {
        // Translate English value to the full v2 value in target locale
        // URL values are in format "Label | ID" (e.g., "Stainless steel | STSTL")
        // v2 facets are in format "ID | Label" (e.g., "STSTL | Acero inoxidable")
        // We need to extract the ID from the URL value and find the matching v2 value
        let englishId = valueLower;
        const pipeIndex = value.indexOf('|');
        if (pipeIndex !== -1) {
          // Extract ID from URL format - ID is after the pipe for regular facets
          // Format: "Stainless steel | STSTL" -> ID is "STSTL"
          const parts = value.split('|').map((p) => p.trim());
          // ID is the part after the pipe (index 1)
          englishId = (parts[1] || parts[0]).toLowerCase();
        }
        const targetV2Value = targetIdToV2Value.get(englishId);
        if (targetV2Value) {
          return targetV2Value;
        }
        // Fallback: try matching the full value (might already be in v2 format or just an ID)
        const targetV2ValueFull = targetIdToV2Value.get(valueLower);
        if (targetV2ValueFull) {
          return targetV2ValueFull;
        }
      }
      // Fallback: return original value if translation not found
      return value;
    })
    .filter(Boolean);
}

/**
 * Transforms locale-specific refinement list values to match the target locale.
 * This is used when writing URL params - translates from current locale values to English.
 *
 * Uses v2 facets (with embedded English IDs) when available for reliable translation,
 * falls back to index-based translation for backwards compatibility.
 */
function transformRefinementListForLocale(
  searchState: SearchUiState[string],
  targetLocaleCode: string,
  searchStateByServerState: InstantSearchServerState['initialResults'][string],
): Record<string, any> {
  const transformedRefinementList: Record<string, any> = {};
  const targetLocale = targetLocaleCode.toUpperCase();
  const facets = searchStateByServerState?.results?.[0]?.facets;

  Object.entries(searchState.refinementList || {}).forEach(([key, values]) => {
    const localePattern = /^(.+)\.([A-Z]{2}(_[A-Z]{2,4})?)$/;
    const match = key.match(localePattern);

    if (match && Array.isArray(values) && values.length > 0) {
      const [, fieldName, sourceLocale] = match;
      const targetKey = `${fieldName}.${targetLocale}`;
      // Try both EN_US and EN_GB as fallbacks if the primary target doesn't exist
      const alternateTargetLocale =
        targetLocale === 'EN_US'
          ? 'EN_GB'
          : targetLocale === 'EN_GB'
            ? 'EN_US'
            : null;
      const alternateSourceLocale =
        sourceLocale === 'EN_US'
          ? 'EN_GB'
          : sourceLocale === 'EN_GB'
            ? 'EN_US'
            : null;

      const alternateKey = alternateTargetLocale
        ? `${fieldName}.${alternateTargetLocale}`
        : null;
      const targetFacet =
        facets?.[targetKey] || (alternateKey ? facets?.[alternateKey] : null);

      if (targetFacet) {
        const sourceFacet = facets?.[key];
        if (!sourceFacet) {
          transformedRefinementList[key] = values;
          return;
        }

        // Try v2 facets first for ID-based translation (more reliable)
        const sourceV2Key = getV2FacetKey(fieldName, sourceLocale);
        const targetV2Key = getV2FacetKey(fieldName, targetLocale);
        const alternateSourceV2Key = alternateSourceLocale
          ? getV2FacetKey(fieldName, alternateSourceLocale)
          : null;
        const alternateTargetV2Key = alternateTargetLocale
          ? getV2FacetKey(fieldName, alternateTargetLocale)
          : null;

        const sourceV2Facet =
          facets?.[sourceV2Key] ||
          (alternateSourceV2Key ? facets?.[alternateSourceV2Key] : null);
        const targetV2Facet =
          facets?.[targetV2Key] ||
          (alternateTargetV2Key ? facets?.[alternateTargetV2Key] : null);

        if (sourceV2Facet && targetV2Facet) {
          // Use v2 ID-based translation
          const translatedValues = translateUsingV2Facets(
            values,
            sourceV2Facet,
            targetV2Facet,
            'toEnglish',
          );
          transformedRefinementList[key] = translatedValues;
        } else {
          // Fallback to index-based translation
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              `[Algolia Translation] Falling back to index-based translation for field "${fieldName}". ` +
                `v2 facets not available (source: ${sourceV2Key}, target: ${targetV2Key}). ` +
                `This may cause incorrect translations if facet ordering differs between locales.`,
            );
          }

          const renderingContent =
            searchStateByServerState?.results?.[0]?.renderingContent;
          const targetOrderPrimary =
            renderingContent?.facetOrdering?.values?.[targetKey]?.order;
          const targetOrderAlternate = alternateKey
            ? renderingContent?.facetOrdering?.values?.[alternateKey]?.order
            : undefined;
          const targetOrder = targetOrderPrimary || targetOrderAlternate;

          const targetKeys = targetOrder || Object.keys(targetFacet);

          const translatedValues = getTranslatedValues(
            values,
            targetKeys as string[],
          );

          transformedRefinementList[key] = translatedValues;
        }
      } else {
        transformedRefinementList[key] = values;
      }
    } else {
      transformedRefinementList[key] = values;
    }
  });

  return transformedRefinementList;
}

export const buildQueryParamsFromSearchUiState = (
  uiState: SearchUiState,
  indexName?: string,
  query?: ParsedUrlQuery,
  serverState?: InstantSearchServerState,
  targetLocaleCode?: string, // NEW: e.g., 'ES' to override all locale-specific keys
): Record<string, string> => {
  const queryParams = new Map();

  const indexId = Object.keys(uiState)[0];
  let searchState = indexName ? uiState[indexName] : uiState[indexId];

  const searchStateByServerState = indexName
    ? serverState?.initialResults[indexName]
    : serverState?.initialResults[indexId];

  // NEW: If targetLocaleCode is provided, transform locale-specific refinement values
  if (
    targetLocaleCode &&
    searchState?.refinementList &&
    searchStateByServerState
  ) {
    const transformedRefinementList = transformRefinementListForLocale(
      searchState,
      targetLocaleCode,
      searchStateByServerState,
    );

    searchState = {
      ...searchState,
      refinementList: transformedRefinementList,
    };
  }
  // Get the key from Algolia that supports the query If not then use the alternative
  const searchableAttributesByServerState =
    searchStateByServerState?.results?.[0].renderingContent?.facetOrdering
      ?.facets?.order;
  const searchableAttributes = searchableAttributesByServerState ?? [
    'collection',
    'type',
    'type_en',
    'size',
    'case',
    'dial_color',
    'lens_color',
    'lens_color_en',
    'frame_color',
    'frame_shape_en',
    'frame_material',
    'distance_between_horns',
    'material',
    'color',
    'strap_length',
    'buckle_type',
  ];

  searchableAttributes?.forEach((field: string) => {
    if (searchState?.refinementList) {
      const matchedKey = Object.keys(searchState?.refinementList)?.find(
        (key: string) => {
          // Match logic for specific fields
          switch (field) {
            case 'dial_color':
              // Match keys starting with "dial_color" but exclude those containing "color"
              return matchesField('dial_color', key, 'color');
            // case 'color':
            //   // Match keys starting with "color" but exclude those containing "dial_color"
            //   return matchesField('color', key, 'dial_color');
            case 'lens_color':
              // Match keys starting with "lens_color" specifically
              return matchesField('lens_color', key);
            case 'frame_color':
              // Match keys starting with "frame_color" specifically
              return matchesField('frame_color', key);
            case 'frame_material':
              return matchesField('frame_material', key);
            case 'size':
              // Match keys starting with "size" but exclude those containing "lug_size"
              return matchesField('size', key, 'lug_size');
            case 'lug_size':
              return matchesField('lug_size', key, 'size');
            default:
              // Default case: Match keys starting with the field name
              return matchesField(field, key);
          }
        },
      );
      if (matchedKey) {
        queryParams.set(field.replace(/\..*?$/, ''), [
          ...searchState.refinementList[
            matchedKey as keyof typeof searchState.refinementList
          ],
        ]);
      }
    }
  });

  if (searchState?.refinementList?.collection) {
    queryParams.set('collection', searchState.refinementList.collection);
  }
  if (searchState?.refinementList?.distance_between_horns_en) {
    queryParams.set(
      'distance_between_horns_en',
      searchState.refinementList.distance_between_horns_en,
    );
  }
  if (searchState?.refinementList?.objectID) {
    queryParams.set(
      'objectID',
      searchState.refinementList.objectID.map((string) =>
        string.trim().toUpperCase(),
      ),
    );
  }

  // Also handle eyewear-specific refinements
  if (searchState?.refinementList?.type_en) {
    queryParams.set('type_en', searchState.refinementList.type_en);
  }
  if (searchState?.refinementList?.lens_color_en) {
    queryParams.set('lens_color_en', searchState.refinementList.lens_color_en);
  }
  // Handle the new lens_color attribute with region code
  const lensColorKey = Object.keys(searchState?.refinementList || {}).find(
    (key) => key.startsWith('lens_color.') || key === 'lens_color',
  );
  if (lensColorKey && searchState?.refinementList?.[lensColorKey]) {
    queryParams.set('lens_color', searchState.refinementList[lensColorKey]);
  }
  // Handle the frame_color attribute with region code
  const frameColorKey = Object.keys(searchState?.refinementList || {}).find(
    (key) => key.startsWith('frame_color.') || key === 'frame_color',
  );
  if (frameColorKey && searchState?.refinementList?.[frameColorKey]) {
    queryParams.set('frame_color', searchState.refinementList[frameColorKey]);
  }
  if (searchState?.refinementList?.frame_shape_en) {
    queryParams.set(
      'frame_shape_en',
      searchState.refinementList.frame_shape_en,
    );
  }

  if (searchState?.range?.price) {
    const price = searchState.range.price;
    const priceParts = price?.split(':');

    if (priceParts[0]) {
      queryParams.set('priceFrom', priceParts[0]);
    }

    if (priceParts[1]) {
      queryParams.set('priceTo', priceParts[1]);
    }
  }

  if (searchState?.sortBy) {
    if (searchState.sortBy.endsWith('price_asc')) {
      queryParams.set('sort-by', 'price_asc');
    }
    if (searchState.sortBy.endsWith('price_desc')) {
      queryParams.set('sort-by', 'price_desc');
    }
    if (searchState.sortBy.endsWith('newest')) {
      queryParams.set('sort-by', 'newest');
    }
  }

  const page = searchState?.page;
  const pageNumber = page ?? 1;
  if (pageNumber > 1) {
    queryParams.set('page', pageNumber);
  }

  if (searchState?.query) {
    let query = searchState.query;
    if (Array.isArray(query)) {
      query = query.join(',');
    }
    if (query.trim() !== '') {
      queryParams.set('q', query);
    }
  }

  const mutableSearchableAttributes = [...searchableAttributes];
  const newQuery = removedSearchableQueryParamsFromCurrentQuery(
    mutableSearchableAttributes,
    query,
  );

  return {
    ...newQuery,
    ...Object.fromEntries(queryParams),
  };
};

// This allow us to return the query params that are not from the filters (queries for trackign for instance)
export const removedSearchableQueryParamsFromCurrentQuery = (
  searchableAttributes: string[],
  query?: ParsedUrlQuery,
) => {
  if (!query) return {};

  const newQuery = { ...query };
  searchableAttributes.forEach((attribute) => {
    delete newQuery[attribute.split('.')[0]];
  });
  delete newQuery['collection'];
  delete newQuery['distance_between_horns_en'];
  delete newQuery['objectID'];
  delete newQuery['priceFrom'];
  delete newQuery['priceTo'];
  delete newQuery['sort-by'];
  delete newQuery['page'];
  delete newQuery['q'];
  // Remove eyewear attributes
  delete newQuery['type_en'];
  delete newQuery['lens_color_en'];
  delete newQuery['lens_color'];
  delete newQuery['frame_color'];
  delete newQuery['frame_shape_en'];

  return newQuery;
};

export const buildUiStateFromURL = (
  path: string | null | undefined,
  initIndexName: string,
  currentIndexName?: string,
  shortIndex?: string,
  locale?: Locale,
  serverState?: InstantSearchServerState,
  sourceLocaleCode?: string,
): SearchUiState => {
  const routeState = parseUrlToRouteState(path);

  return buildUiStateFromRouteState(
    routeState,
    initIndexName,
    currentIndexName,
    shortIndex,
    locale,
    serverState,
    sourceLocaleCode,
  );
};

export const buildUiStateFromRouteState = (
  routeState: Record<string, any>,
  initIndexName: string,
  currentIndexName?: string,
  shortIndex?: string,
  locale?: Locale,
  serverState?: InstantSearchServerState,
  sourceLocaleCode?: string,
): SearchUiState => {
  const regionCode = locale?.code?.toUpperCase() || 'EN_US';

  const refinementList = {
    collection: routeState['collection'] ?? [],
    case: routeState['case'] ?? [],
    dial_color: routeState['dial_color'] ?? [],
    size: routeState['size'] ?? [],
    type: routeState['type'] ?? [],
    type_en: routeState['type_en'] ?? [],
    lens_color: routeState['lens_color'] ?? [],
    frame_color: routeState['frame_color'] ?? [],
    frame_material: routeState['frame_material'] ?? [],
    lens_color_en: routeState['lens_color_en'] ?? [],
    frame_shape_en: routeState['frame_shape_en'] ?? [],
    distance_between_horns_en: routeState['distance_between_horns_en'] ?? [],
    material: routeState['material'] ?? [],
    color: routeState['color'] ?? [],
    strap_length: routeState['strap_length'] ?? [],
    buckle_type: routeState['buckle_type'] ?? [],
    objectID: routeState['objectID'] ?? [],
    lug_type: routeState['lug_type'] ?? [],
  };

  // Get facets for reverse translation if needed
  const indexResults = serverState?.initialResults?.[initIndexName];
  const results = indexResults?.results;
  const firstResult = results?.[0];
  const facets = firstResult?.facets;

  const updatedRefinementList: Record<string, any> = Object.entries(
    refinementList,
  ).reduce((acc: Record<string, any>, [key, value]) => {
    if (
      [
        'size',
        'collection',
        'distance_between_horns_en',
        'type_en',
        'lens_color_en',
        'frame_shape_en',
      ]?.includes(key)
    ) {
      acc[key] = value;
    } else if (key === 'objectID') {
      acc[key] = value.map((string: string) => string.trim().toUpperCase());
    } else {
      const updatedKey = `${key}.${regionCode}`;

      // Reverse translate if sourceLocaleCode is provided and different from current locale
      if (
        sourceLocaleCode &&
        sourceLocaleCode !== regionCode &&
        facets &&
        Array.isArray(value) &&
        value.length > 0
      ) {
        const sourceLocaleUpper = sourceLocaleCode.toUpperCase();
        // Try both EN_US and EN_GB as fallbacks for both source and target
        const alternateSourceLocale =
          sourceLocaleUpper === 'EN_US'
            ? 'EN_GB'
            : sourceLocaleUpper === 'EN_GB'
              ? 'EN_US'
              : null;
        const alternateTargetLocale =
          regionCode === 'EN_US'
            ? 'EN_GB'
            : regionCode === 'EN_GB'
              ? 'EN_US'
              : null;

        const sourceKey = `${key}.${sourceLocaleUpper}`;
        const alternateSourceKey = alternateSourceLocale
          ? `${key}.${alternateSourceLocale}`
          : null;
        const alternateTargetKey = alternateTargetLocale
          ? `${key}.${alternateTargetLocale}`
          : null;

        const targetFacet =
          facets[updatedKey] ||
          (alternateTargetKey ? facets[alternateTargetKey] : null);
        const sourceFacet =
          facets[sourceKey] ||
          (alternateSourceKey ? facets[alternateSourceKey] : null);

        if (targetFacet && sourceFacet) {
          // Try v2 facets first for ID-based translation (more reliable)
          const sourceV2Key = getV2FacetKey(key, sourceLocaleUpper);
          const targetV2Key = getV2FacetKey(key, regionCode);
          const alternateSourceV2Key = alternateSourceLocale
            ? getV2FacetKey(key, alternateSourceLocale)
            : null;
          const alternateTargetV2Key = alternateTargetLocale
            ? getV2FacetKey(key, alternateTargetLocale)
            : null;

          const sourceV2Facet =
            facets[sourceV2Key] ||
            (alternateSourceV2Key ? facets[alternateSourceV2Key] : null);
          const targetV2Facet =
            facets[targetV2Key] ||
            (alternateTargetV2Key ? facets[alternateTargetV2Key] : null);

          if (sourceV2Facet && targetV2Facet) {
            // Use v2 ID-based translation (English URL values -> locale UI values)
            const translatedValues = translateUsingV2Facets(
              value,
              sourceV2Facet,
              targetV2Facet,
              'fromEnglish',
            );
            acc[updatedKey] = translatedValues;
          } else {
            // Fallback to index-based translation
            if (process.env.NODE_ENV === 'development') {
              console.warn(
                `[Algolia Translation] Falling back to index-based translation for field "${key}". ` +
                  `v2 facets not available (source: ${sourceV2Key}, target: ${targetV2Key}). ` +
                  `This may cause incorrect translations if facet ordering differs between locales.`,
              );
            }

            const renderingContent = firstResult?.renderingContent;

            const targetOrderPrimary =
              renderingContent?.facetOrdering?.values?.[updatedKey]?.order;
            const targetOrderAlternate = alternateTargetKey
              ? renderingContent?.facetOrdering?.values?.[alternateTargetKey]
                  ?.order
              : undefined;
            const targetOrder = targetOrderPrimary || targetOrderAlternate;

            const targetKeys = targetOrder || Object.keys(targetFacet);

            const translatedValues = getTranslatedValues(
              value,
              targetKeys as string[],
            );

            acc[updatedKey] = translatedValues;
          }
        } else {
          acc[updatedKey] = value;
        }
      } else {
        acc[updatedKey] = value;
      }
    }
    return acc;
  }, {});

  const formatPrice = (price: any) =>
    isNaN(Number(price)) ? '' : String(Number(price));
  const priceFrom = formatPrice(routeState['priceFrom']);
  const priceTo = formatPrice(routeState['priceTo']);

  const range = { price: `${priceFrom}:${priceTo}` };

  let sortBy = '';
  const sortByParam =
    typeof routeState['sort-by'] === 'string'
      ? routeState['sort-by']
      : routeState['sort-by']?.[0];

  if (sortByParam) {
    // sort need test
    if (['price_asc', 'price_desc', 'newest']?.includes(sortByParam)) {
      sortBy = `${currentIndexName ?? initIndexName}.${routeState['sort-by']}`;
    }
  }

  const page = getIntParam(routeState['page']);

  const state = {
    refinementList: updatedRefinementList,
    range,
    sortBy,
    page,
    query: routeState['q'] ?? '',
  };

  return {
    [initIndexName]: state,
    ...(shortIndex ? { [shortIndex]: state } : undefined),
  };
};
export const getIndexNameByCountry = (
  type: ALGOLIA_INDEX_DATA_TYPE,
  ctxLocale: string,
  countryFromPDP: Country,
): string => {
  const { country } = getRegionFromLocale(ctxLocale);
  const rightCountry = countryFromPDP ?? country;
  if (type === 'STORE') {
    return `${ENVIRONMENT_NAME}.${type}`;
  } else if (type === 'SERVICE_CENTER') {
    return `${ENVIRONMENT_NAME}.SERVICE_CENTER`;
  }
  return `${ENVIRONMENT_NAME}.${type}.${rightCountry.channelSlug}`;
};

export const getIndexName = (
  type: ALGOLIA_INDEX_DATA_TYPE,
  ctxLocale: string,
): string => {
  const { country } = getRegionFromLocale(ctxLocale);
  if (type === 'STORE') {
    return `${ENVIRONMENT_NAME}.STORE`;
  } else if (type === 'SERVICE_CENTER') {
    return `${ENVIRONMENT_NAME}.SERVICE_CENTER`;
  }
  return `${ENVIRONMENT_NAME}.PRODUCT.${country.channelSlug}`;
};

export const isWatchHit = (
  b?: WatchHit | StrapHit | EyewearHit,
): b is WatchHit => {
  return b?.product_type === 'Watch';
};

export const isStrapHit = (
  b?: WatchHit | StrapHit | EyewearHit,
): b is StrapHit => {
  return b?.product_type === 'Strap';
};

export const isEyewearHit = (
  b?: WatchHit | StrapHit | EyewearHit,
): b is EyewearHit => {
  return b?.product_type === 'Eyewear';
};

export const onlySupportedStrapMaterialsFilter = supportedStrapMaterials
  .map((material) => `material:'${material}'`)
  .join(' OR ');

export const performMultiSearch = async <TObj = SearchHit>(
  query: string,
  locale: string,
  params?: SearchOptions,
  draftMode?: boolean,
  code?: CountryCode,
  queryType?: 'prefixLast' | 'prefixAll' | 'prefixNone' | undefined,
  typoTolerance?: boolean | 'min' | 'strict' | undefined,
  userToken?: string,
) => {
  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
  });

  return algoliaSearchClient.multipleQueries<TObj>([
    {
      indexName: getIndexName('PRODUCT', locale),
      query,
      params: {
        ...params,
        filters:
          params?.filters ??
          applyProductConfigurationFilters(
            ProductConfigurationFilterType.SEARCH,
            'Watch',
            draftMode,
            '',
          ),
        typoTolerance: typoTolerance,
        queryType: queryType ?? 'prefixLast',
        ...(userToken && { userToken }),
      },
    },
    {
      indexName: getIndexName('PRODUCT', locale),
      query,
      params: {
        ...params,
        filters:
          params?.filters ??
          applyProductConfigurationFilters(
            ProductConfigurationFilterType.SEARCH,
            'Strap',
            draftMode,
            onlySupportedStrapMaterialsFilter,
          ),
        ...(userToken && { userToken }),
      },
    },
    {
      indexName: getIndexName('STORE', locale),
      query,
      params: {
        filters: `web_visible:true`,
        ...(userToken && { userToken }),
      },
    },
  ]);
};

export enum ProductConfigurationFilterType {
  PLP = 'plp',
  PDP = 'pdp',
  SEARCH = 'search',
  COMPONENTS = 'components',
  WIDGET = 'widget',
}

export const WATCH_SKU_REGEX = /[A-Z]{1,2}\d{4,8}[A-Z]{1}\d{1}[A-Z]{1}\d{1}/i;

export function getWatchFilters(
  query: string | string[] | undefined,
  defaultFilters: string,
): [string, boolean[]] {
  if (!query) {
    return [defaultFilters, [false]];
  }

  const pattern = WATCH_SKU_REGEX;
  if (Array.isArray(query)) {
    const filters = [];
    const isWatchSkuArray = [];
    for (const q of query) {
      const isWatchSku = pattern.test(q);
      if (isWatchSku) {
        filters.push(`objectID: ${q}`);
      }
      isWatchSkuArray.push(isWatchSku);
    }
    const filterString =
      filters.length > 0 ? `${defaultFilters}` : defaultFilters;

    return [filterString, isWatchSkuArray];
  } else {
    const isWatchSku = pattern.test(query);
    const filterString = isWatchSku ? `${defaultFilters}` : defaultFilters;
    return [filterString, [isWatchSku]];
  }
}

/**
 * Checks if a query matches an eyewear SKU pattern and creates filter based on that
 * Example eyewear SKUs: JZ310615.25, JZ310615_25
 * Pattern: Two letters followed by 6 numbers, then a dot or underscore, then 2 numbers
 */
export function getEyewearFilters(
  query: string | string[] | undefined,
  defaultFilters: string,
): [string, boolean[]] {
  if (!query) {
    return [defaultFilters, [false]];
  }

  const pattern = /^[A-Z]{2}\d{6}[._]\d{2}$/i;
  if (Array.isArray(query)) {
    const filters = [];
    const isEyewearSkuArray = [];
    for (const q of query) {
      const isEyewearSku = pattern.test(q);
      if (isEyewearSku) {
        filters.push(`objectID: ${q}`);
      }
      isEyewearSkuArray.push(isEyewearSku);
    }
    const filterString =
      filters.length > 0 ? `${defaultFilters}` : defaultFilters;

    return [filterString, isEyewearSkuArray];
  } else {
    const isEyewearSku = pattern.test(query);
    const filterString = isEyewearSku ? `${defaultFilters}` : defaultFilters;
    return [filterString, [isEyewearSku]];
  }
}

const hasTopLevelOr = (filter: string): boolean => {
  if (!filter) return false;
  let depth = 0;
  for (let i = 0; i < filter.length; i++) {
    if (filter[i] === '(') depth++;
    else if (filter[i] === ')') depth--;
    else if (depth === 0 && filter.slice(i, i + 4) === ' OR ') return true;
  }
  return false;
};

export const applyProductConfigurationFilters = (
  type: ProductConfigurationFilterType,
  productType?: ProductType,
  draftMode: boolean | undefined = false,
  originalFilters = '',
  zeroTime: boolean | undefined = false,
) => {
  if (productType === 'Store') {
    return originalFilters;
  }
  const currentUnixTimestamp = zeroTime
    ? Math.floor(dayjs().utc().startOf('date').toDate().getTime() / 1000)
    : Math.floor(new Date().getTime() / 1000);

  const useProductConfigurator =
    process.env.NEXT_PUBLIC_USE_PRODUCT_CONFIGURATOR === 'true';

  let productConfigurationFilters = originalFilters;

  if (productType && productType !== null) {
    const safeOriginalFilters = hasTopLevelOr(originalFilters)
      ? `(${originalFilters})`
      : originalFilters;
    productConfigurationFilters =
      safeOriginalFilters && safeOriginalFilters.length > 0
        ? `product_type:${productType} AND ${safeOriginalFilters}`
        : `product_type:${productType}`;
  }

  if (useProductConfigurator) {
    const releaseFilter = `${type}_release_ts < ${currentUnixTimestamp} AND ${type}_expire_ts > ${currentUnixTimestamp}`;
    const previewFilter = `${type}_preview=1`;
    // Algolia forbids (X AND Y) OR Z at any level. The distributive equivalent
    // (A AND B) OR C ≡ (A OR C) AND (B OR C) produces only (X OR Y) AND Z groups.
    const activationFilter = draftMode
      ? `(${type}_release_ts < ${currentUnixTimestamp} OR ${previewFilter}) AND (${type}_expire_ts > ${currentUnixTimestamp} OR ${previewFilter})`
      : releaseFilter;

    productConfigurationFilters =
      productConfigurationFilters && productConfigurationFilters.trim() !== ''
        ? `${productConfigurationFilters} AND ${activationFilter}`
        : activationFilter;
  }

  return productConfigurationFilters.replace(
    'objectID:objectID:',
    'objectID:',
  ) as string;
};

export const filterHits = (results: any, draftMode: boolean) => {
  if (results?.results === undefined) {
    return [];
  }
  return results?.results.filter((hit: any) => {
    if (!hit) return false;

    const currentUnixTimestamp = Math.floor(new Date().getTime() / 1000);
    const releaseKey = 'components_release_ts';
    const expireKey = 'components_expire_ts';
    const previewKey = 'components_preview';
    const isPublished =
      hit[releaseKey] < currentUnixTimestamp &&
      hit[expireKey] > currentUnixTimestamp;

    const isPreview = hit[previewKey];

    const isWithinTimeRange = draftMode
      ? isPublished || isPreview
      : isPublished;

    return isWithinTimeRange;
  });
};

export const filterEnabledStraps = async (
  country: Country,
  languageCode: LanguageCodeEnum,
  bundleVariationSkus: string[],
  draftMode = false,
) => {
  const indexName = getIndexNameByCountry(
    'PRODUCT',
    languageCode ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
    country,
  );

  try {
    const client = createSearchClient({ isPreview: draftMode });
    const index = client.initIndex(indexName);
    const { results } = await index.getObjects<WatchHit>(bundleVariationSkus);
    return (results || [])
      .filter(
        (hit): hit is WatchHit =>
          Boolean(hit) &&
          (hit?.pdp_enabled === true ||
            (draftMode && hit?.pdp_preview === true)) &&
          checkProductActivation(hit, draftMode).isPdpActivated,
      )
      .map((hit) => hit.objectID);
  } catch (err) {
    console.error(
      'Error filtering enabled (product configurator) straps:',
      err,
    );
    return bundleVariationSkus;
  }
};

//SSR ONLY
export const searchWatchesBySku = async (
  sku: string,
  ctxLocale: string,
  draftMode?: boolean,
) => {
  const algoliaSsrSearchClient = createSearchClient({
    isPreview: draftMode,
    isSsr: true,
  });
  const index = algoliaSsrSearchClient.initIndex(
    getIndexName('PRODUCT', ctxLocale),
  );
  return index.search<WatchHit>(`${sku}`, {
    filters: `product_type:Watch`,
    queryType: 'prefixNone',
  });
};

export const getDesignerChoiceByPartialSku = async (
  sku: string,
  ctxLocale: string,
  draftMode?: boolean,
) => {
  const data = await searchWatchesBySku(sku, ctxLocale, draftMode);
  const len = data.hits.length;
  let result = null;
  if (len > 0) {
    for (let i = 0; i < len; i++) {
      const item = data.hits[i];
      if (item.designer_choice && item.objectID?.includes(sku)) {
        result = item;
        break;
      }
    }
  }
  return result;
};

type UrlRouteStateValue = string | string[];

export const parseUrlToRouteState = (
  url?: string | null,
): Record<string, UrlRouteStateValue> => {
  const params = new URLSearchParams(getSearchStringFromAsPath(url));
  const routeState: Record<string, UrlRouteStateValue> = {};

  params.forEach((value, key) => {
    const existing = routeState[key];
    if (existing) {
      if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        routeState[key] = [existing, value];
      }
    } else {
      routeState[key] = value;
    }
  });

  Object.keys(routeState).forEach((key) => {
    const value = routeState[key];
    if (!Array.isArray(value) && key !== 'q') {
      routeState[key] = [value];
    }
  });

  return routeState;
};

function filterReleasedOrDraftProducts(
  hits: AlgoliaHit[],
  filterType: ProductConfigurationFilterType,
  productType?: ProductType,
  zeroTime: boolean | undefined = false,
  draftMode: boolean | undefined = false,
) {
  const currentUnixTimestamp = zeroTime
    ? Math.floor(dayjs().utc().startOf('date').toDate().getTime() / 1000)
    : Math.floor(new Date().getTime() / 1000);

  const results = hits.filter((hit) => {
    if (!hit || (productType && hit.product_type !== productType)) {
      return false;
    }

    const isReleased =
      `${filterType}_release_ts` in hit &&
      hit[`${filterType}_release_ts`] < currentUnixTimestamp &&
      `${filterType}_expire_ts` in hit &&
      hit[`${filterType}_expire_ts`] > currentUnixTimestamp;

    const isPreview = draftMode && hit[`${filterType}_preview`];

    return isReleased || isPreview;
  });
  return results;
}

export const getFilterReleasedProducts = async <T extends ProductHit>(
  skus: string[],
  locale: string,
  productType?: ProductType,
  filterType = ProductConfigurationFilterType.PLP,
  indexName?: string,
  isSsr = false,
  draftMode: boolean | undefined = false,
) => {
  const _indexName = indexName ?? getIndexName('PRODUCT', locale);
  const client = createSearchClient({
    isSsr,
    isPreview: draftMode,
  });

  const index = client.initIndex(_indexName);

  const hits = (await index.getObjects<T>(skus)).results.filter(
    (item) => !!item,
  ) as T[];

  return filterReleasedOrDraftProducts(
    hits,
    filterType,
    productType,
    false,
    draftMode,
  ) as T[];
};

export async function getProduct<T extends WatchHit | StrapHit | EyewearHit>(
  locale: string | undefined,
  sku: string,
  client: SearchClient,
): Promise<T | null> {
  const indexName = getIndexName(
    'PRODUCT',
    locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  );

  const index = client.initIndex(indexName);

  try {
    return await index.getObject<T>(sku);
  } catch (error) {
    return null;
  }
}

export async function getProducts<T extends WatchHit | StrapHit | EyewearHit>(
  locale: string | undefined,
  skus: string[],
  client: SearchClient,
): Promise<(T | null)[]> {
  const indexName = getIndexName(
    'PRODUCT',
    locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
  );
  const index = client.initIndex(indexName);
  const response = await index.getObjects<T>(skus);
  return response.results;
}

export function checkTimestamps(
  expireTs: number | null | undefined,
  releaseTs: number | null | undefined,
  now: number = Math.floor(new Date().getTime() / 1000),
) {
  if (expireTs && expireTs <= now) return false;
  if (releaseTs && releaseTs >= now) return false;
  if (expireTs && releaseTs && expireTs < releaseTs) return false;
  return true;
}

export function checkProductActivation(
  hit: WatchHit | StrapHit | EyewearHit | undefined | null,
  draftMode: undefined | boolean,
) {
  const useProductConfigurator =
    process.env.NEXT_PUBLIC_USE_PRODUCT_CONFIGURATOR === 'true';

  if (!hit) {
    return {
      isPdpActivated: false,
      isPlpActivated: false,
      isSearchActivated: false,
    };
  }

  if (!useProductConfigurator) {
    return {
      isPdpActivated: true,
      isPlpActivated: true,
      isSearchActivated: true,
    };
  }

  return {
    isPdpActivated:
      checkTimestamps(hit.pdp_expire_ts, hit.pdp_release_ts) ||
      (draftMode ? hit.pdp_preview : false),
    isPlpActivated:
      checkTimestamps(hit.plp_expire_ts, hit.plp_release_ts) ||
      (draftMode ? hit.plp_preview : false),
    isSearchActivated:
      checkTimestamps(hit.search_expire_ts, hit.search_release_ts) ||
      (draftMode ? hit.search_preview : false),
  };
}

const ACTIVE_FILTER_KEYS = [
  'collection',
  'type',
  'type_en',
  'size',
  'case',
  'dial_color',
  'lens_color',
  'lens_color_en',
  'frame_color',
  'frame_shape_en',
  'frame_material',
  'distance_between_horns',
  'material',
  'color',
  'strap_length',
  'buckle_type',
  'priceFrom',
  'priceTo',
  'sort-by',
  'objectID',
  'q',
] as const;

export const useHasActiveFilters = () => {
  const { asPath, query } = useRouter();

  return useMemo(() => {
    const searchString = getSearchStringFromAsPath(asPath);
    const urlParams = new URLSearchParams(searchString);
    const queryKeys = Object.keys(query);
    const paramKeys = [...Array.from(urlParams.keys()), ...queryKeys];

    return ACTIVE_FILTER_KEYS.some(
      (filter) =>
        urlParams.has(filter) ||
        queryKeys.includes(filter) ||
        paramKeys.some((key) => key.includes(filter)),
    );
  }, [asPath, query]);
};

export function getTranslatedValues(values: string[], targetKeys: string[]) {
  return values
    .map((val: string) => {
      // Try matching by ID if value contains pipe (URL format)
      const valPipeIndex = val.indexOf('|');
      if (valPipeIndex !== -1) {
        // Extract ID from URL format - ID is after the pipe
        const parts = val.split('|').map((p) => p.trim());
        const valId = (parts[1] || parts[0]).toLowerCase();
        // Find target value with matching ID
        const matchingTarget = targetKeys.find((targetVal) => {
          const targetPipeIndex = targetVal.indexOf('|');
          if (targetPipeIndex !== -1) {
            const targetParts = targetVal.split('|').map((p) => p.trim());
            const targetId = (targetParts[1] || targetParts[0]).toLowerCase();
            return targetId === valId;
          }
          return false;
        });
        if (matchingTarget) {
          return matchingTarget;
        }
      }
      return val;
    })
    .filter(Boolean);
}
